import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "breethe_popup_v1";
const SHOW_DELAY_MS = 7000;
const HIDDEN_PATHS = ["/admin", "/payment", "/manage", "/review", "/ambassador"];
const HIDDEN_EXACT = new Set(HIDDEN_PATHS);
const isHidden = (path) =>
  HIDDEN_EXACT.has(path) || HIDDEN_PATHS.some((p) => path.startsWith(`${p}/`));

const EmailPopup = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("form"); // form | loading | done
  const [code, setCode] = useState("");
  const [theme] = useState(() => localStorage.getItem("breethe-theme") || "dark");

  const hidden = isHidden(location.pathname);

  useEffect(() => {
    if (hidden || localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [hidden]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setVisible(false);
  };

  const submit = async () => {
    if (!email.trim()) return;
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/popup/subscribe`, { email: email.trim() });
      setCode(data.code);
      setState("done");
      localStorage.setItem(STORAGE_KEY, "claimed");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Enter a valid email address.");
      setState("form");
    }
  };

  if (!visible || hidden) return null;

  return (
    <div
      data-testid="email-popup"
      data-theme={theme}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md border border-volt bg-[#121212] p-8 md:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="popup-close"
          onClick={dismiss}
          aria-label="Close offer"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-white/50 transition-colors duration-200 hover:text-volt"
        >
          <X size={18} />
        </button>

        {state !== "done" ? (
          <>
            <p className="font-plex text-[10px] uppercase tracking-[0.3em] text-volt">
              First order offer
            </p>
            <h3 className="mt-3 font-anton uppercase text-3xl md:text-4xl leading-[0.95] text-white">
              Get 10% off<br />your first order
            </h3>
            <p className="mt-4 text-white/60 font-light text-sm leading-relaxed">
              Drop your email and your single-use code appears instantly. No spam — just the
              occasional drop.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="YOUR EMAIL"
              data-testid="popup-email-input"
              className="mt-6 w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300"
            />
            <button
              onClick={submit}
              disabled={state === "loading"}
              data-testid="popup-submit-button"
              className="mt-4 w-full h-13 py-4 bg-volt text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
            >
              {state === "loading" ? "Creating your code…" : "Get my 10% off"}
            </button>
          </>
        ) : (
          <>
            <p className="font-plex text-[10px] uppercase tracking-[0.3em] text-volt">
              You're in
            </p>
            <h3 className="mt-3 font-anton uppercase text-3xl leading-[0.95] text-white">
              Your code:
            </h3>
            <div
              data-testid="popup-code"
              className="mt-5 border border-white/25 py-4 text-center font-plex text-xl tracking-[0.2em] text-white select-all"
            >
              {code}
            </div>
            <p className="mt-4 text-white/60 font-light text-sm leading-relaxed">
              10% off your first order — single use. Enter it at checkout, or just hit the button
              and we'll have it ready.
            </p>
            <a
              href={`/buy?ref=${code}`}
              data-testid="popup-shop-link"
              className="mt-4 w-full py-4 bg-volt text-black font-anton uppercase tracking-wider text-base flex items-center justify-center transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98]"
            >
              Shop with my code
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailPopup;
